import React, { useEffect, useState } from 'react';
import {
  Plus,
  RefreshCw,
  Wallet,
  DollarSign,
  Users,
  Briefcase,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import PageHeader from '../../../components/common/PageHeader';
import StatsCard from '../../../components/common/StatsCard';
import EmptyState from '../../../components/common/EmptyState';
import StatusChip from '../components/shared/StatusChip';
import LoadingSkeleton from '../components/shared/LoadingSkeleton';
import PAMMErrorState from '../components/shared/PAMMErrorState';
import { DataTable } from '../../../components/ui/data-table';
import PammDrawerForm from '../components/shared/PammDrawerForm';
import SourceCombobox from '../../../components/common/SourceCombobox';
import { pammDepositService, pammFundService } from '../services/pammService';
import { formatCurrency, exportToCSV } from '../utils';
import type { PAMMFund } from '../types';
import type { DataTableColumn } from '../../../components/ui/data-table';
import { validatePammInvestmentForm } from '../../../utils/validationMessages';
import toast from 'react-hot-toast';
import { Input } from '../../../components/ui';

export interface InvestmentItem {
  id: string;
  pammName: string;
  pammId: string;
  amount: number;
  mt5Id: string;
  investorPassword?: string;
  totalProfit: number;
  profitWithdrawn: number;
  status: 'active' | 'pending' | 'completed';
  createdDate: string;
}

export default function Deposits() {
  const [investments, setInvestments] = useState<InvestmentItem[]>([]);
  const [availablePamms, setAvailablePamms] = useState<PAMMFund[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const walletBalance = 10000;
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedPammId, setSelectedPammId] = useState('');
  const [investmentAmountInput, setInvestmentAmountInput] = useState('');
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);

      const [fundsData, depositsData] = await Promise.all([
        pammFundService.getAll(),
        pammDepositService.getAll(),
      ]);

      setAvailablePamms(fundsData);
      const mappedInvestments: InvestmentItem[] = depositsData.map(
        (d, idx) => ({
          id: d.id || `INV-00${idx + 1}`,
          pammName: d.fundName || 'PAMM Strategy',
          pammId: d.fundId || `fund-${idx + 1}`,
          amount: d.amount || 1000,
          mt5Id: `MT5-883920${idx + 1}`,
          investorPassword: '••••••••',
          totalProfit: idx % 2 === 0 ? 450.0 : 120.0,
          profitWithdrawn: idx % 2 === 0 ? 100.0 : 0.0,
          status: 'active',
          createdDate: d.requestDate || new Date().toISOString(),
        })
      );

      if (mappedInvestments.length === 0) {
        const dummyInvestments: InvestmentItem[] = [
          {
            id: 'INV-001',
            pammName: 'Gold Scalper Master',
            pammId: 'fund-1',
            amount: 5000,
            mt5Id: 'MT5-8839201',
            investorPassword: '••••••••',
            totalProfit: 450.0,
            profitWithdrawn: 100.0,
            status: 'active',
            createdDate: '2024-01-15',
          },
          {
            id: 'INV-002',
            pammName: 'Forex Alpha Trend',
            pammId: 'fund-2',
            amount: 3500,
            mt5Id: 'MT5-8839202',
            investorPassword: '••••••••',
            totalProfit: 280.0,
            profitWithdrawn: 50.0,
            status: 'active',
            createdDate: '2024-02-01',
          },
          {
            id: 'INV-003',
            pammName: 'Conservative Multi-Asset',
            pammId: 'fund-3',
            amount: 2000,
            mt5Id: 'MT5-8839203',
            investorPassword: '••••••••',
            totalProfit: 110.0,
            profitWithdrawn: 0.0,
            status: 'active',
            createdDate: '2024-02-10',
          },
        ];
        setInvestments(dummyInvestments);
      } else {
        setInvestments(mappedInvestments);
      }
    } catch {
      setError('Failed to load investments.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const selectedPamm = availablePamms.find(p => p.id === selectedPammId);
  const numericAmount = parseFloat(investmentAmountInput) || 0;
  const balanceAfterInvestment = Math.max(0, walletBalance - numericAmount);

  const handleAddInvestmentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validation = validatePammInvestmentForm({
      pammId: selectedPammId,
      investmentAmount: investmentAmountInput,
      walletBalance,
      minInvestment: selectedPamm?.minimumInvestment,
      maxInvestment: selectedPamm?.maximumInvestment,
    });

    if (!validation.valid) {
      setFormErrors(validation.errors);
      return;
    }

    setFormErrors({});
    setIsSubmitting(true);

    try {
      const newInv: InvestmentItem = {
        id: `INV-00${investments.length + 1}`,
        pammName: selectedPamm?.name || 'Selected PAMM',
        pammId: selectedPammId,
        amount: numericAmount,
        mt5Id: `MT5-88392${investments.length + 4}`,
        investorPassword: '••••••••',
        totalProfit: 0,
        profitWithdrawn: 0,
        status: 'active',
        createdDate: new Date().toISOString().split('T')[0],
      };

      setInvestments(prev => [newInv, ...prev]);
      toast.success('PAMM Investment added successfully.');
      setIsDrawerOpen(false);
      setSelectedPammId('');
      setInvestmentAmountInput('');
    } catch {
      toast.error('Failed to add investment. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  const totalNetProfit = investments.reduce((s, i) => s + i.totalProfit, 0);
  const totalActiveInvestment = investments.reduce((s, i) => s + i.amount, 0);

  const columns: DataTableColumn<InvestmentItem>[] = [
    {
      id: 'id',
      header: 'ID',
      accessor: row => (
        <span className="font-mono text-xs font-semibold text-foreground">
          {row.id}
        </span>
      ),
      sortable: true,
      sortValue: row => row.id,
    },
    {
      id: 'pammName',
      header: 'PAMM Name',
      accessor: row => (
        <span className="font-medium text-xs text-foreground block truncate max-w-[160px]">
          {row.pammName}
        </span>
      ),
      sortable: true,
      sortValue: row => row.pammName,
    },
    {
      id: 'amount',
      header: 'Amount',
      accessor: row => (
        <span className="text-xs font-semibold text-foreground">
          {formatCurrency(row.amount)}
        </span>
      ),
      sortable: true,
      sortValue: row => row.amount,
    },
    {
      id: 'mt5Id',
      header: 'MT5 ID',
      accessor: row => (
        <span className="font-mono text-xs font-medium">{row.mt5Id}</span>
      ),
    },
    {
      id: 'investorPassword',
      header: 'Investor Password',
      accessor: row => (
        <span className="text-xs font-mono text-muted-foreground">
          {row.investorPassword || '••••••••'}
        </span>
      ),
    },
    {
      id: 'totalProfit',
      header: 'Total Profit',
      accessor: row => (
        <span
          className={`text-xs font-semibold ${row.totalProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}
        >
          {row.totalProfit >= 0
            ? `+${formatCurrency(row.totalProfit)}`
            : formatCurrency(row.totalProfit)}
        </span>
      ),
      sortable: true,
      sortValue: row => row.totalProfit,
    },
    {
      id: 'profitWithdrawn',
      header: 'Profit Withdrawn',
      accessor: row => (
        <span className="text-xs text-muted-foreground">
          {formatCurrency(row.profitWithdrawn)}
        </span>
      ),
      sortable: true,
      sortValue: row => row.profitWithdrawn,
    },
    {
      id: 'status',
      header: 'Status',
      accessor: row => <StatusChip status={row.status} size="sm" />,
    },
  ];

  if (loading) {
    return (
      <div className="space-y-6">
        <PageHeader title="Investments" />
        <LoadingSkeleton type="cards" cols={4} />
        <LoadingSkeleton type="table" rows={6} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <PageHeader title="Investments" />
        <PAMMErrorState message={error} onRetry={load} />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-6">
      <PageHeader
        title="Investments"
        description="Track your PAMM investments, profits, and balances"
      >
        <Button
          size="sm"
          className="bg-primary/90 hover:bg-primary/95 text-white shadow-sm font-medium"
          onClick={() => {
            setFormErrors({});
            setIsDrawerOpen(true);
          }}
        >
          <Plus className="h-4 w-4 mr-1.5" />
          Add New Investment
        </Button>
      </PageHeader>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatsCard
          title="Wallet Balance"
          value={formatCurrency(walletBalance)}
          icon={Wallet}
        />
        <StatsCard
          title="Net Profit"
          value={formatCurrency(totalNetProfit)}
          icon={DollarSign}
        />
        <StatsCard
          title="Active PAMM"
          value={investments.length}
          icon={Users}
        />
        <StatsCard
          title="Active Investment"
          value={formatCurrency(totalActiveInvestment)}
          icon={Briefcase}
        />
      </div>

      {/* Action Bar & Table */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-2">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load}>
            <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
            Refresh
          </Button>
        </div>
      </div>

      {investments.length === 0 ? (
        <EmptyState
          icon={Briefcase}
          title="No Investments Found"
          description="Click + Add New Investment to get started."
        />
      ) : (
        <DataTable
          columns={columns}
          data={investments}
          searchValue={searchValue}
          showColumnVisibility={true}
          searchPlaceholder="Search..."
          onSearchChange={setSearchValue}
          onSearchReset={() => setSearchValue('')}
          showExportButton={true}
          exportButtonText="Export CSV"
          onExportClick={() => exportToCSV(investments, 'pamm_investments')}
        />
      )}

      {/* Add Pamm Investment Drawer */}
      <PammDrawerForm
        open={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        title="Add Pamm Investment"
        onSubmit={handleAddInvestmentSubmit}
        loading={isSubmitting}
        submitText="Add"
      >
        {/* Select PAMM */}
        <div className="space-y-1.5">
          <label className="block text-xs font-semibold text-foreground">
            Select PAMM <span className="text-red-500">*</span>
          </label>
          <SourceCombobox
            items={availablePamms.map(p => ({
              value: p.id,
              label: `${p.name} (${p.managerName})`,
            }))}
            placeholder="Select PAMM Account"
            value={selectedPammId}
            onChange={val => {
              setSelectedPammId(val);
              setFormErrors(prev => ({ ...prev, pammId: '' }));
            }}
          />
          {formErrors.pammId && (
            <p className="text-xs text-red-500 mt-1">{formErrors.pammId}</p>
          )}
        </div>

        {/* Current Wallet Balance */}
        <div className="pt-1">
          <p className="text-sm font-semibold text-foreground">
            Current Wallet Balance: {formatCurrency(walletBalance)}
          </p>
        </div>

        {/* Balance After Investment */}
        <div>
          <p className="text-sm font-semibold text-emerald-600 dark:text-emerald-400">
            Balance After Investment: {formatCurrency(balanceAfterInvestment)}
          </p>
        </div>

        {/* Investment Amount */}
        <div className="space-y-1.5 pt-1">
          <label className="block text-xs font-semibold text-foreground">
            Investment Amount <span className="text-red-500">*</span>
          </label>
          <Input
            type="number"
            placeholder="0.00"
            value={investmentAmountInput}
            onChange={e => {
              setInvestmentAmountInput(e.target.value);
              setFormErrors(prev => ({ ...prev, investmentAmount: '' }));
            }}
            className="w-full bg-background border border-input rounded-lg px-3.5 py-2.5 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-all"
          />
          {formErrors.investmentAmount ? (
            <p className="text-xs text-red-500 mt-1">
              {formErrors.investmentAmount}
            </p>
          ) : (
            <p className="text-xs text-muted-foreground mt-1">
              Enter the amount you want to invest
            </p>
          )}
        </div>
      </PammDrawerForm>
    </div>
  );
}
